import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  FieldPolicy,
  FieldReadFunction,
  HttpLink,
  InMemoryCache,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { cacheSizes } from '@apollo/client/utilities'
import { createStandaloneToast } from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import { TokenRefreshLink } from 'apollo-link-token-refresh'
import { jwtDecode, JwtPayload } from 'jwt-decode'

import { log } from '@common/log/log'
import { ErrorCode } from '@common/util/errorCodes'
import { sortAscending, sortDescending } from '@common/util/sort'
import { LocalStorageKey } from '~/components/hooks/useLocalStorage'
import { getWriteMode } from '~/data/adminWriteMode'
import { UserFragment } from '~/graphql/generated/query.types'

import { getAccessToken, setAccessToken } from './accessToken'

const { ToastContainer, toast } = createStandaloneToast()

export const ToastComponent = ToastContainer

// for Chrome devtools / local debugging convenience
const windowAny = typeof window !== 'undefined' ? window : ({} as any)
const adminData = windowAny?.localStorage?.getItem(LocalStorageKey.ADMIN_DATA)
const isImpersonating = adminData !== null && adminData !== undefined
if (isImpersonating) log.info('admin[isImpersonating]')

// on large property pages this cache will get to ~73k right now to test in the
// future you can do `apolloClient.getMemoryInternals?.()` check what the limits
// are and what the sizes are currently no nother parts of our cache are close
// to their limits
cacheSizes['inMemoryCache.executeSelectionSet'] = 100000

const httpLink = new HttpLink({
  uri: `${process.env.NEXT_PUBLIC_BASE_API_URL}/graphql`,
  credentials: 'include',
  fetch: (uri: any, options: any) => {
    const reqBody = JSON.parse(options.body as string)
    const opName = reqBody.operationName
    return fetch(`${uri}?opName=${opName}`, {
      ...options,
      headers: { authorization: `bearer ${getAccessToken()}`, ...(options.headers || {}) },
    })
  },
})

const adminWriteModeCheck = new ApolloLink((operation, forward) => {
  // If `Fetch` is first part of the operationName, we know it's a GQL `query` and allowable
  if (operation?.operationName?.startsWith('Fetch') || getWriteMode() === true) {
    // Allow operation because it's a GET or WriteMode is enabled.
    return forward(operation)
  }
  // Otherwise, block the operation
  log.info('adminLink[blocked]', operation.operationName)
  toast({
    title: 'Write mode disabled',
    description: `You must enable impersonation write mode to perform the ${operation.operationName} action.`,
    status: 'warning',
    duration: 5000,
    isClosable: true,
  })
  throw new Error('Write mode disabled')
})

function paginatedTypePolicy(
  keyArgs: string[] | undefined = undefined,
  sortOrder = 'asc',
  options?: {
    /** Field used to order cached items when reading. Defaults to timeCreated. */
    sortField?: string
    /**
     * When true, a refetch without a cursor replaces cached pages instead of
     * merging into them. Use for lists that load more but do not poll, so a
     * first-page refetch cannot retain stale later pages.
     */
    replaceOnRefetch?: boolean
  }
): FieldPolicy<any> | FieldReadFunction<any> {
  const sortField = options?.sortField ?? 'timeCreated'
  const replaceOnRefetch = options?.replaceOnRefetch ?? false
  return {
    keyArgs: keyArgs ?? false,
    // keyFields: [],

    merge(existing, incoming, { readField, args }) {
      const isFetchMore = Boolean(args?.cursor)
      // On refetch (no cursor), optionally replace rather than merge so stale
      // pages from a prior fetchMore are not retained.
      const shouldReplace = replaceOnRefetch && !isFetchMore
      const items = existing && !shouldReplace ? { ...existing.items } : {}
      const incomingItems = readField('items', incoming) as any[]
      incomingItems.forEach((item: any) => {
        const index = readField('id', item)! || readField('sid', item)!
        items[index.toString()] = item
      })
      // Only advance the cursor when this is a fetchMore (has cursor arg).
      // Poll/refetch calls have no cursor arg and would reset pagination —
      // unless replaceOnRefetch cleared the prior pages, in which case the
      // incoming first-page cursor is authoritative.
      const cursor = isFetchMore
        ? incoming.cursor
        : shouldReplace
          ? incoming.cursor
          : (existing?.cursor ?? incoming.cursor)
      return {
        __typename: readField('__typename', incoming),
        cursor,
        items,
      }
    },

    read(existing, { readField }) {
      if (existing) {
        const sortFn = sortOrder === 'asc' ? sortAscending : sortDescending
        return {
          __typename: existing.__typename,
          cursor: existing.cursor,
          items: sortFn(Object.values(existing.items), (item: any) =>
            readField<string>(sortField, item)
          ),
        }
      }
    },
  }
}

const cache = new InMemoryCache({
  // https://www.apollographql.com/docs/react/caching/cache-configuration/#customizing-cache-ids
  dataIdFromObject(responseObject) {
    if (responseObject.id || responseObject.sid) {
      return `${responseObject.__typename}:${responseObject.id || responseObject.sid}`
    }
    return defaultDataIdFromObject(responseObject)
  },

  typePolicies: {
    Query: {
      fields: {
        actions: paginatedTypePolicy(
          ['includeAdminActions', 'portfolioSid', 'propertySid'],
          'desc'
        ),
        chatConversationsForAdmin: paginatedTypePolicy(['firmSid', 'search'], 'desc', {
          sortField: 'timeUpdated',
          replaceOnRefetch: true,
        }),
        crawls: paginatedTypePolicy(),
        jobs: paginatedTypePolicy([], 'desc'),
        jobQueues: paginatedTypePolicy(['firmSid', 'name'], 'desc'),
        syncJobQueues: paginatedTypePolicy(['portfolioSid', 'propertySid'], 'desc'),
      },
    },
  },
})

const links = [
  new TokenRefreshLink({
    accessTokenField: 'accessToken',

    isTokenValidOrUndefined: async () => {
      const token = getAccessToken()

      if (!token) {
        return false
      }

      try {
        const { exp } = jwtDecode<JwtPayload>(token)
        if (Date.now() >= exp! * 1000) {
          return false
        }
        return true
      } catch {
        return false
      }
    },
    fetchAccessToken: () => {
      return fetch(`${process.env.NEXT_PUBLIC_BASE_API_URL}/refresh_token`, {
        method: 'POST',
        credentials: 'include',
      })
    },
    handleFetch: accessToken => {
      setAccessToken(accessToken)
    },
    handleError: err => {
      console.warn('Your refresh token is invalid. Try to relogin')
      console.error(err)
    },
  }),
  onError(({ graphQLErrors, networkError }) => {
    // TODO (benbayard): We should probably add a lookup which
    // forces us to include all the error codes that we want to handle here.
    // something like: type ErrorCodeLookup = Record<ErrorCode, {isHandled: boolean}>
    const handledErrorCodes = new Set<string>([
      ErrorCode.DUPLICATE_OWNERSHIP_GROUP_NAME,
      ErrorCode.LEASE_UP_INVALID_STABILIZED_OCCUPANCY,
      ErrorCode.LEASE_UP_INVALID_MINIMUM_LEASED_PERCENT,
      ErrorCode.LEASE_UP_INVALID_MONTHLY_GOAL_MONTH_OFFSET,
      ErrorCode.LEASE_UP_INVALID_MONTHLY_GOAL_RATIO,
      ErrorCode.LEASE_UP_DUPLICATE_MONTHLY_GOAL_MONTH_OFFSET,
      ErrorCode.LEASE_UP_MONTHLY_GOAL_OUT_OF_RANGE,
      ErrorCode.LEASE_UP_PLAN_ALREADY_EXISTS,
      ErrorCode.LEASE_UP_PROPERTY_NOT_IN_LEASE_UP_MODE,
      ErrorCode.LEASE_UP_PLAN_NOT_EDITABLE,
      ErrorCode.LEASE_UP_TARGET_DATE_NOT_AFTER_PRELEASE,
      ErrorCode.LEASE_UP_TARGET_DATE_NOT_AFTER_PLAN_START,
    ])
    const errors: string[] = []
    if (graphQLErrors) {
      graphQLErrors.map(({ message, path }) => {
        const err = `${path}: ${message}`
        console.error(`[GraphQL error]: ${err}`)
        if (handledErrorCodes.has(message)) {
          return
        }
        Sentry.captureException(new Error(err))
        errors.push(err)
      })
    }
    if (networkError) {
      const err = `${networkError.name} ${networkError.message}`
      if (`${err}`.includes('Received status code 403')) {
        return // don't toast; this is a valid 403 error when the 15min access token expires
      }
      console.error(`[GraphQL network error]: ${err}`)
      errors.push(err)
    }

    if (errors.length > 0) {
      const token = getAccessToken()
      if (!token) {
        return // defensive check, the token should be defined
      }

      const user = jwtDecode<UserFragment>(token)
      if (user?.admin) {
        const msg = errors.join('\n')
        // deduplication on message string, we don't need to show the same error twice
        if (!toast.isActive(msg)) {
          toast({
            id: msg,
            title: 'Error',
            duration: null,
            description: errors.join('\n'),
            status: 'error',
            isClosable: true,
          })
        }
      }
    }
  }),
  // requestLink,
  httpLink,
]

// If we're an admin and impersonating someone, add our `writeMode`
// middleware that will always verify that write is enabled before allowing
// any `non-Fetch` queries.
if (isImpersonating) links.unshift(adminWriteModeCheck)
const link = ApolloLink.from(links)

// consider using https://github.com/adamsoffer/next-apollo
// https://www.rockyourcode.com/nextjs-with-apollo-ssr-cookies-and-typescript/
// https://hasura.io/learn/graphql/nextjs-fullstack-serverless/apollo-client/
export const apolloClient = new ApolloClient({
  link,
  cache,

  // queryDeduplication: false,
  // defaultOptions: {
  //   query: {
  //     fetchPolicy: 'cache-first',
  //   },
  // },
})

windowAny.apolloCache = cache
windowAny.apolloClient = apolloClient
